/**
 * Raw hotspot detail panel (E.W.0.5). A PRE-FUSION satellite thermal anomaly from
 * the E.1 hotspot_detections table — the lower-confidence sibling of the F.1-scored
 * High-Risk Hotspots (operator-cop-doctrine §9.1).
 *
 * Deliberately has NO R.3 click-fire actions (Confirm Fire / Recommend Dispatch):
 * raw hotspots are pre-fusion SIGNALS, not actionable incidents. They feed the F.1
 * fusion engine, which produces the actionable high_risk_hotspots. When a co-located
 * fused hotspot exists (within 1 km), the panel offers a link to open it.
 */
import { useEffect, useState } from 'react';
import type { RawHotspotFeature, RawHotspotDetail } from '@tsr/shared-types';
import { fetchRawHotspotDetail } from './api';
import { rawHotspotStyle } from './styles/rawHotspots';
import { logAction } from './lib/operatorActions';

function agoLabel(iso: string | null): string {
  if (!iso) return 'unknown';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** A coarse confidence band, paired with the numeric value (never colour-alone). */
function confidenceBand(confidence: number | null, raw: string | null): string {
  if (raw === 'l') return 'low';
  if (raw === 'n') return 'nominal';
  if (raw === 'h') return 'high';
  if (confidence == null) return 'unknown';
  if (confidence < 30) return 'low';
  if (confidence < 80) return 'nominal';
  return 'high';
}

export function RawHotspotBody({
  hotspot,
  onOpenFusedHotspot,
}: {
  hotspot: RawHotspotFeature;
  /** Open the co-located F.1-scored hotspot (when one exists within 1 km). */
  onOpenFusedHotspot?: (id: string) => void;
}): JSX.Element {
  const p = hotspot.properties;
  const [lon, lat] = hotspot.geometry.coordinates;
  const style = rawHotspotStyle(p.source);
  const [detail, setDetail] = useState<RawHotspotDetail | null>(null);

  useEffect(() => {
    logAction('view_raw_hotspot_detail', { hotspot_id: hotspot.id, source: p.source });
  }, [hotspot.id, p.source]);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchRawHotspotDetail(hotspot.id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [hotspot.id]);

  const fused = detail?.properties.fused_hotspot_nearby ?? null;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="raw-hotspot-body">
      <div className="border-l-2 pl-3" style={{ borderColor: style.color }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Thermal anomaly</p>
        <h2 className="text-base font-semibold">{style.label}</h2>
        <p className="text-xs text-slate-400 mt-0.5">Observed {agoLabel(p.observed_at)}</p>
        <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-700/60 text-slate-300">
          Pre-fusion · raw satellite detection
        </span>
      </div>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Detection</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">FRP</dt>
          <dd className="font-mono">{p.frp != null ? `${p.frp.toFixed(1)} MW` : '—'}</dd>
          <dt className="text-slate-400">Confidence</dt>
          <dd className="font-mono">
            {p.confidence != null ? `${Math.round(p.confidence)} / 100` : '—'} ·{' '}
            {confidenceBand(p.confidence, p.confidence_raw)}
          </dd>
          {p.satellite && (
            <>
              <dt className="text-slate-400">Satellite</dt>
              <dd>{p.satellite}</dd>
            </>
          )}
          {p.sensor && (
            <>
              <dt className="text-slate-400">Sensor</dt>
              <dd>{p.sensor}</dd>
            </>
          )}
          {(p.scan != null || p.track != null) && (
            <>
              <dt className="text-slate-400">Scan × track</dt>
              <dd className="font-mono">
                {p.scan != null ? p.scan.toFixed(2) : '—'} × {p.track != null ? p.track.toFixed(2) : '—'} km
              </dd>
            </>
          )}
          {detail?.properties.fwi != null && (
            <>
              <dt className="text-slate-400">Fire-weather index</dt>
              <dd className="font-mono">{detail.properties.fwi.toFixed(1)}</dd>
            </>
          )}
          <dt className="text-slate-400">Position</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      {fused && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Fusion</h3>
          <p className="text-[11px] text-slate-400 mb-2 leading-relaxed">
            A co-located AI-fused hotspot exists ~{fused.distance_m} m away — this raw signal
            has a scored sibling.
          </p>
          <button
            type="button"
            onClick={() => onOpenFusedHotspot?.(fused.id)}
            className="w-full rounded bg-amber-600/80 hover:bg-amber-500 px-3 py-2 text-sm font-medium"
          >
            Open F.1 Fused Hotspot (conf {fused.confidence} · L{fused.response_level}) →
          </button>
        </section>
      )}

      <section className="border-t border-slate-800 pt-3">
        <p className="text-[11px] text-slate-400 leading-relaxed">
          Raw thermal is a <strong>pre-fusion</strong> signal: a satellite saw heat here, but it has
          not been scored or corroborated. It is not an actionable incident — it feeds the F.1 fusion
          engine, which correlates it against other signals to produce the actionable high-risk
          hotspots in Urgent Alerts. No dispatch action is available here.
        </p>
      </section>

      <footer className="border-t border-slate-800 pt-3 text-[10px] text-slate-500 leading-relaxed space-y-0.5">
        <div>
          Source: {style.label}
          {p.source ? ` (${p.source})` : ''}
          {detail?.properties.agency ? ` · ${detail.properties.agency}` : ''}
        </div>
        {detail?.properties.ingested_at && <div>Ingested {agoLabel(detail.properties.ingested_at)}</div>}
        <div>NASA FIRMS / NRCan CWFIS / NOAA GOES-R — multi-source satellite thermal feeds.</div>
      </footer>
    </div>
  );
}
